import {
  format,
  parse,
  parseISO,
  differenceInYears,
  differenceInMonths,
  differenceInDays,
} from "date-fns";
import { clearLoggedInUserDataInLocalStorage } from "../config/config";
import { emitSessionExpired } from "./sessionEvents";
import CryptoJS from "crypto-js";
import { ar, enUS } from "date-fns/locale";

// Exact calendar age in completed years.
// Accepts Date | ISO string | YYYY-MM-DD. Returns null if invalid/missing.
export const calculateAge = (dob) => {
  if (!dob) return null;
  const birth = dob instanceof Date ? dob : new Date(dob);
  if (isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age;
};

// Family-policy upper-age cap: members aged 66+ are ineligible.
export const isFamilyAgeIneligible = (dob) => {
  const age = calculateAge(dob);
  return age !== null && age >= 66;
};

// Travel-policy upper-age cap: anyone older than 85 cannot issue a travel policy.
export const isTravelAgeIneligible = (dob) => {
  const age = calculateAge(dob);
  return age !== null && age > 85;
};

// Travel-policy Schengen-only band: ages 81 through 85 (inclusive)
// may only issue the Schengen plan.
export const isTravelSchengenOnlyAge = (dob) => {
  const age = calculateAge(dob);
  return age !== null && age >= 81 && age <= 85;
};

export const expireSoonCalculator = (endDate, days) => {
  const end = new Date(endDate);
  const today = new Date();
  const timeDiff = end - today;
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

  return daysDiff < days && daysDiff > 0 ? daysDiff : null;
};
export const daysCalculator = (startDate, endDate) => {
  const end = new Date(endDate);
  const start = new Date(startDate);
  const timeDiff = end - start;
  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
  // Travel duration should be return date - departure date + 1
  return daysDiff + 1;
};

export const formatDate = (dateString) => {
  try {
    const dateObj = parseISO(dateString);
    return format(dateObj, "d MMMM yyyy");
  } catch (error) {
    return "";
  }
};

export function formatToThreeDigits(value) {
  if (typeof value != "number" || isNaN(value)) {
    return 0;
  }
  return Number(value.toFixed(3));
}

export const isNumeric = (value) => {
  return /^\d*$/.test(value);
};
export const isAlphabetsAndSpaces = (value) => {
  return /^[a-zA-Z\s]*$/.test(value);
};

export const MMDDformatter = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 2) {
    return digits;
  } else if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  } else {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}`;
  }
};

export const mockApiCall = (response) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(response);
    }, 2000);
  });
};

export const percentageWith2Decimals = (value) => {
  return Number(value || 0).toFixed(2) + "%";
};
export const With2Decimals = (value) => {
  return Number(value || 0).toFixed(2);
};
export const perCalcMinus100 = (value) => {
  if (!value && value != 0) return "NA";
  else {
    return Number((value || 0) - 100).toFixed(2) + "%";
  }
};
export const generateQueryString = (data) => {
  const queryString = new URLSearchParams(data).toString();
  return queryString;
};
export const decodeQueryString = () => {
  const queryString = window.location.search;
  console.log(queryString, "some query string");
  const params = new URLSearchParams(queryString);
  const data = {};
  params.forEach((value, key) => {
    data[key] = value;
  });
  return data;
};
export const dateFormatter = (date, dateFormatType = "d MMM yyyy", locale) => {
  // Format date in '1 Dec 2020' format use  dateFormatType = "d MMM yyyy"
  // Format date in '1 December 2020' format use  dateFormatType = "d MMMM yyyy"
  // Format date in 'Dec 2020' format use  dateFormatType = "MMM yyyy"
  if (!date) return null;
  let parsedDate;
  // Attempt parsing with "dd/MM/yyyy HH:mm:ss" format first
  try {
    parsedDate =
      date instanceof Date
        ? date
        : parse(date, "dd/MM/yyyy HH:mm:ss", new Date());
  } catch (error) {
    console.log("Failed to parse with time, trying without time...");
  }

  // If parsing with time failed or date is invalid, try "dd/MM/yyyy" format
  if (!parsedDate || isNaN(parsedDate.getTime())) {
    parsedDate =
      date instanceof Date ? date : parse(date, "dd/MM/yyyy", new Date());
  }

  return format(parsedDate, dateFormatType, { locale: locale =="ar" ? ar : enUS });
};

// Helper function to calculate date differences in years or days
export const calculateDateDifference = (start, end, format, yearLabel) => {
  if (!start || !end) return null;

  const startDate = parse(start, "dd/MM/yyyy HH:mm:ss", new Date());
  const endDate = parse(end, "dd/MM/yyyy HH:mm:ss", new Date());

  if (format =="years") {
    return `${differenceInYears(endDate, startDate)} ${yearLabel}`;
  } else if (format =="days") {
    return `${differenceInDays(endDate, startDate)} days`;
  }

  return null;
};

// Calculate remaining term in 'X years Y months' format
export const calculateYearsMonthsRemaining = (start, end, yearTrasnlation = "years", monthTranslation = "months") => {
  if (!start || !end) return null;

  // Check if start and end are Date objects or strings
  const startDate =
    start instanceof Date
      ? start
      : parse(start, "dd/MM/yyyy HH:mm:ss", new Date());
  const endDate =
    end instanceof Date ? end : parse(end, "dd/MM/yyyy HH:mm:ss", new Date());

  const years = differenceInYears(endDate, startDate);
  const months = differenceInMonths(endDate, startDate) - years * 12; // Calculate the remaining months

  return `${years} ${yearTrasnlation} ${months} ${monthTranslation}`;
};
export const getTodayAndMonthsBack = (monthsBack = 12) => {
  const today = new Date();

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const pastDate = new Date();
  pastDate.setMonth(today.getMonth() - monthsBack);

  return {
    today: formatDate(today),
    pastDate: formatDate(pastDate),
  };
};

export const formatDate2 = (value) => {
  const formattedPastDate = format(value, "MMM yyyy");
  return formattedPastDate;
};

export const intFromBhdCurrency = (currentValue) => {
  return currentValue?.length > 4
    ? currentValue?.slice(4, currentValue?.length)
    : currentValue;
};

export const getCurrencyAndValue = (currentValue) => {
  // Validate input
  if (!currentValue || typeof currentValue != "string") {
    return { value: "0", country_code: "" };
  }

  const parts = currentValue.split(" ");

  if (parts.length ==2) {
    // Case: "BHD 12345"
    return {
      country_code: parts[0],
      value: parts[1],
    };
  } else if (parts.length ==1) {
    // Case: "12345" (no country code)
    return {
      country_code: "", // No country code present
      value: parts[0],
    };
  }

  // Fallback for unexpected cases
  return { value: "0", country_code: "" };
};

export const currencyFormatter = (amount, locale = "en-US") => {
  try {
    const numAmount = Number(amount);

    if (isNaN(numAmount)) {
      return amount;
    }

    const isWholeNumber = numAmount ==Math.floor(numAmount);
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: isWholeNumber ? 0 : 2, // Omit decimals for whole numbers
      maximumFractionDigits: 2, // Show up to 2 decimals for fractional numbers
    }).format(numAmount);
  } catch (error) {
    console.error("Error formatting number:", error);
    return amount;
  }
};

export const currencyFormatterWithCountryCode = (x, locale = "en-US") => {
  let { value, country_code } = getCurrencyAndValue(x);
  let formattedValue = currencyFormatter(value, locale);
  return `${country_code} ${formattedValue}`;
};

export const clearLocalStorageForUnauthorized = () => {
  // Announce first, while the outgoing JWT is still readable. The push module
  // uses this to release the device's FCM registration — without it the token
  // stays bound to the expired user, and the next person to sign in on this
  // device keeps receiving their notifications.
  emitSessionExpired();
  clearLoggedInUserDataInLocalStorage();
  window.location.href = "/";
};

export const removeKeysFromObject = (obj, keysToRemove) => {
  return Object.keys(obj)
    .filter(key => !keysToRemove.includes(key))
    .reduce((result, key) => {
      result[key] = obj[key];
      return result;
    }, {});
};

/**
 * Format number values with thousand separators and 3 decimal places
 * Ideal for premium totals, basic amounts, and other insurance-related values
 *
 * @param {number|string} value - The value to format
 * @param {string} locale - Locale for formatting ('en-US' or 'en-US')
 * @param {number} decimals - Number of decimal places (default: 3)
 * @returns {string} Formatted number string
 *
 * Examples:
 * formatPremiumValue(40000) => "40,000.000" (en-US)
 * formatPremiumValue(4000.5) => "4,000.500" (en-US)
 * formatPremiumValue(40000, 'en-US') => "٤٠٬٠٠٠٫٠٠٠" (en-US)
 */
export const formatPremiumValue = (value, locale = "en-US", decimals = 3) => {
  try {
    // Convert to number
    const numValue = Number(value);

    // Return 0 for invalid values
    if (isNaN(numValue) || value === null || value === undefined) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(0);
    }

    // Format with Intl.NumberFormat for locale-aware formatting
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(numValue);
  } catch (error) {
    console.error("Error formatting premium value:", error);
    return value?.toString() || "0.000";
  }
};

/**
 * Format premium value with currency code (e.g., "BHD")
 * Currency can be passed directly or as a translation string from i18n
 * For Arabic currency codes (د.ب), the currency is placed after the amount
 *
 * @param {number|string} value - The value to format
 * @param {string} currencyCode - Currency code or translated currency (e.g., "BHD", t('currency'))
 * @param {string} locale - Locale for formatting
 * @param {number} decimals - Number of decimal places (default: 3)
 * @returns {string} Formatted string with currency code
 *
 * Example:
 * formatPremiumWithCurrency(40000, "BHD") => "BHD 40,000.000"
 * formatPremiumWithCurrency(40000, "د.ب") => "40,000.000 د.ب" (Arabic format)
 */
export const formatPremiumWithCurrency = (
  value,
  currencyCode = "",
  locale = "en-US",
  decimals = 3
) => {
  const formattedValue = formatPremiumValue(value, locale, decimals);
  
  if (!currencyCode) {
    return formattedValue;
  }
  
  // For Arabic currency symbols, place currency after the amount
  if (currencyCode === "د.ب" || currencyCode.includes("د.ب")) {
    return `${formattedValue} ${currencyCode}`;
  }
  
  // For English/other currencies, place currency before the amount
  return `${currencyCode} ${formattedValue}`;
};

/**
 * Format all premium fields in an API response object
 * Useful for formatting entire quote responses
 *
 * @param {object} data - API response object
 * @param {array} fields - Array of field names to format
 * @param {string} locale - Locale for formatting
 * @returns {object} Object with formatted values
 *
 * Example:
 * formatPremiumFields(quoteData, ['premium', 'basic_premium', 'total_premium'])
 */
export const formatPremiumFields = (data, fields = [], locale = "en-US") => {
  if (!data || typeof data !== "object") {
    return data;
  }

  const formatted = { ...data };

  fields.forEach((field) => {
    if (formatted[field] !== undefined && formatted[field] !== null) {
      formatted[field] = formatPremiumValue(formatted[field], locale);
    }
  });

  return formatted;
};
// Convert Arabic-Indic and Eastern Arabic-Indic digits to ASCII 0-9 in strings.
export const toEnglishDigits = (value) => {
  if (typeof value !== "string") return value;
  return value
    .replace(/[\u0660-\u0669]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x0660 + 48))
    .replace(/[\u06F0-\u06F9]/g, (d) => String.fromCharCode(d.charCodeAt(0) - 0x06F0 + 48));
};

// Recursively normalize all string values in an object/array to use English digits.
export const normalizeDigitsDeep = (input) => {
  if (input == null) return input;
  if (typeof input === "string") return toEnglishDigits(input);
  if (typeof input !== "object") return input;
  if (
    (typeof File !== "undefined" && input instanceof File) ||
    (typeof Blob !== "undefined" && input instanceof Blob) ||
    (typeof FormData !== "undefined" && input instanceof FormData) ||
    input instanceof Date
  ) {
    return input;
  }
  if (Array.isArray(input)) {
    return input.map((item) => normalizeDigitsDeep(item));
  }
  const out = {};
  for (const key of Object.keys(input)) {
    out[key] = normalizeDigitsDeep(input[key]);
  }
  return out;
};

export const decryptPolicyNumber = (encryptedPolicy) => {
  if (!encryptedPolicy) return encryptedPolicy;
  try {
    const secretKey = "i-hate-meej";
    const base64 = encryptedPolicy.split("-").join("+").split("_").join("/");
    const bytes = CryptoJS.AES.decrypt(base64, secretKey);
    const result = bytes.toString(CryptoJS.enc.Utf8);
    return result || encryptedPolicy;
  } catch (e) {
    return encryptedPolicy;
  }
};
